import React from 'react';
import styled from 'styled-components';
import { Icon } from '../ui/shell';
import { themeVars, flowComponents } from './flowKit';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* ============================================================
   FlowMap — "Workflow" route. The corrected instructor-workflow
   diagram + changelog, ported 1:1 from the reference flowmap.jsx.
   Buttons launch the matching live flow (onReplay / onCreateCourse).
   ============================================================ */

const Wrap = styled.div`
  ${themeVars}
  ${flowComponents}

  .card { background: var(--surface); border-radius: var(--r-lg); box-shadow: var(--card-shadow); border: var(--card-border); }
  .card--pad { padding: var(--pad-card); }
  .section-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; }
  .section-head h2 { font-family: var(--font-head); font-size: 16px; font-weight: 700; margin: 0; letter-spacing: -.01em; }
  .section-head p { margin: 2px 0 0; font-size: var(--fs-sm); color: var(--text-muted); }

  .fm__intro { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; justify-content: space-between; margin-bottom: var(--gap); }
  .fm__legend { display: flex; gap: 16px; flex-wrap: wrap; }
  .fm__legend-item { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 600; color: var(--text-muted); }
  .fm__legend-swatch { width: 13px; height: 13px; border-radius: 4px; flex: none; }

  .fm__lane { margin-bottom: var(--gap); }
  .fm__lane-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
  .fm__lane-num { width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center; font-family: var(--font-head); font-weight: 760; font-size: 14px; background: var(--accent); color: #fff; flex: none; }
  .fm__lane-head h3 { font-family: var(--font-head); font-size: 16px; font-weight: 720; margin: 0; }
  .fm__lane-head p { margin: 1px 0 0; font-size: 12.5px; color: var(--text-muted); }
  .fm__lane-head .btn { margin-left: auto; }

  .fm__flow { display: flex; align-items: center; gap: 0; flex-wrap: wrap; }
  .fm__col { display: flex; flex-direction: column; gap: 8px; justify-content: center; }
  .fm__arrow { display: grid; place-items: center; color: var(--text-subtle); flex: none; padding: 0 4px; align-self: center; }
  .fm__arrow svg { width: 22px; height: 22px; }

  .node { position: relative; min-width: 124px; max-width: 168px; padding: 11px 13px; border: 1.5px solid var(--border); border-radius: 12px; background: var(--surface); font-size: 12.5px; font-weight: 600; line-height: 1.32; color: var(--text); box-shadow: var(--shadow-sm); display: flex; gap: 9px; align-items: flex-start; }
  .node__ico { width: 24px; height: 24px; border-radius: 7px; flex: none; display: grid; place-items: center; background: var(--surface-3); color: var(--text-muted); margin-top: -1px; }
  .node--start { background: var(--rail-bg); color: #fff; border-color: var(--rail-bg); border-radius: 999px; min-width: 0; padding: 9px 18px; justify-content: center; font-weight: 700; }
  .node--end { background: color-mix(in oklch, var(--success) 13%, var(--surface)); border-color: color-mix(in oklch, var(--success) 40%, var(--border)); border-radius: 999px; }
  .node--end .node__ico { background: var(--success); color: #fff; }
  .node--decision { border-radius: 12px; border-style: dashed; border-color: oklch(0.58 0.13 60); background: color-mix(in oklch, var(--warning) 10%, var(--surface)); }
  .node--decision .node__ico { background: color-mix(in oklch, var(--warning) 20%, var(--surface)); color: oklch(0.5 0.13 60); }
  .node--new { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft), var(--shadow-sm); }
  .node--edited { border-color: var(--info); box-shadow: 0 0 0 3px color-mix(in oklch, var(--info) 18%, transparent), var(--shadow-sm); }
  .node__badge { position: absolute; top: -9px; right: -7px; font-size: 9px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; padding: 2px 6px; border-radius: 999px; color: #fff; }
  .node__badge--new { background: var(--accent); }
  .node__badge--edited { background: var(--info); }

  .fm__branch { display: flex; flex-direction: column; gap: 8px; }
  .fm__branch-row { display: flex; align-items: center; gap: 0; }
  .fm__branchlabel { font-size: 10.5px; font-weight: 700; color: var(--text-subtle); background: var(--surface-3); padding: 2px 8px; border-radius: 999px; margin-right: 8px; white-space: nowrap; align-self: center; }

  .fm__changes { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
  .change { display: flex; gap: 12px; padding: 15px 16px; border: 1px solid var(--border); border-radius: var(--r-lg); background: var(--surface); }
  .change__ico { width: 34px; height: 34px; border-radius: 10px; flex: none; display: grid; place-items: center; background: var(--accent-soft); color: var(--accent-strong); }
  .change h4 { margin: 0 0 3px; font-size: 13.5px; font-weight: 700; }
  .change p { margin: 0; font-size: 12.5px; color: var(--text-muted); line-height: 1.5; }
`;

function FNode({ label, icon, kind, badge }) {
  const cls = 'node'
    + (kind === 'start' ? ' node--start' : '')
    + (kind === 'end' ? ' node--end' : '')
    + (kind === 'decision' ? ' node--decision' : '')
    + (badge === 'new' ? ' node--new' : badge === 'edited' ? ' node--edited' : '');
  return (
    <div className={cls}>
      {badge && <span className={'node__badge node__badge--' + badge}>{badge}</span>}
      {kind !== 'start' && icon && <span className="node__ico"><Icon name={icon} size={14} /></span>}
      <span>{label}</span>
    </div>
  );
}

const FArrow = ({ down }) => (
  <span className="fm__arrow"><Icon name={down ? 'down' : 'chevR'} size={20} /></span>
);

function FSeq({ nodes }) {
  return (
    <>
      {nodes.map((n, i) => (
        <React.Fragment key={i}>
          {i > 0 && <FArrow />}
          <FNode {...n} />
        </React.Fragment>
      ))}
    </>
  );
}

function FBranch({ rows }) {
  return (
    <span className="fm__arrow" style={{ padding: 0 }}>
      <Icon name="chevR" size={20} />
      <div className="fm__branch" style={{ marginLeft: 4 }}>
        {rows.map((r, i) => (
          <div className="fm__branch-row" key={i}>
            {r.label && <span className="fm__branchlabel">{r.label}</span>}
            <FSeq nodes={r.nodes} />
          </div>
        ))}
      </div>
    </span>
  );
}

function Lane({ n, title, sub, action, children }) {
  return (
    <div className="fm__lane">
      <div className="fm__lane-head">
        <span className="fm__lane-num">{n}</span>
        <div><h3>{title}</h3><p>{sub}</p></div>
        {action && <button className="btn btn--ghost btn--sm" onClick={action.onClick}><Icon name={action.icon || 'play'} size={14} />{action.label}</button>}
      </div>
      <div className="card card--pad" style={{ overflowX: 'auto' }}>
        <div className="fm__flow" style={{ flexWrap: 'nowrap', width: 'max-content', minWidth: '100%' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function FlowMap({ onReplay, onCreateCourse }) {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  return (
    <>
    <Wrap>
      <div className="fm__intro">
        <div>
          <div className="section-head" style={{ margin: 0 }}>
            <div>
              <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 21, fontWeight: 740, margin: 0, letterSpacing: '-.01em' }}>Instructor workflow</h2>
              <p style={{ margin: '3px 0 0', fontSize: 13.5, color: 'var(--text-muted)' }}>The end-to-end onboarding & course-creation journey, rebuilt and corrected from the original Visio diagram.</p>
            </div>
          </div>
        </div>
        <div className="fm__legend">
          {[['Process', 'var(--surface)', 'var(--border-strong)'], ['Decision', 'color-mix(in oklch, var(--warning) 22%, var(--surface))', 'oklch(0.58 0.13 60)'], ['New', 'var(--accent-soft)', 'var(--accent)'], ['Edited', 'color-mix(in oklch, var(--info) 22%, var(--surface))', 'var(--info)'], ['End', 'color-mix(in oklch, var(--success) 22%, var(--surface))', 'var(--success)']].map(([l, bg, bd]) => (
            <span className="fm__legend-item" key={l}><span className="fm__legend-swatch" style={{ background: bg, boxShadow: 'inset 0 0 0 1.5px ' + bd }} />{l}</span>
          ))}
        </div>
      </div>

      <Lane n="1" title="Account onboarding" sub="First-time login through to choosing a path"
        action={{ label: 'Replay onboarding', icon: 'play', onClick: () => {
          if (!guardAction('action.workflow.replayOnboarding')) return;
          onReplay('intro');
        } }}>
        <FSeq nodes={[
          { label: 'Start', kind: 'start' },
          { label: 'User sign up', icon: 'users' },
          { label: 'Verify email', icon: 'mail' },
          { label: 'First-time login', icon: 'lock', badge: 'edited' },
          { label: 'Intro video', icon: 'play' },
          { label: 'Display options', icon: 'route', kind: 'decision' },
        ]} />
        <FBranch rows={[
          { label: 'Instructor', nodes: [{ label: '→ Lane 4', icon: 'users' }] },
          { label: 'Free trial', nodes: [{ label: '→ Lane 2', icon: 'rocket' }] },
          { label: 'Learner', nodes: [{ label: '→ Lane 5', icon: 'eye' }] },
          { label: 'Contact', nodes: [{ label: '→ Lane 5', icon: 'mail' }] },
        ]} />
      </Lane>

      <Lane n="2" title="Free trial & billing" sub="Plan selection through payment — billing order corrected"
        action={{ label: 'Try trial flow', icon: 'rocket', onClick: () => {
          if (!guardAction('action.workflow.tryTrialFlow')) return;
          onReplay('packages');
        } }}>
        <FSeq nodes={[
          { label: 'Start free trial', icon: 'rocket' },
          { label: 'Display packages', icon: 'layers', badge: 'edited' },
          { label: 'Select a package', icon: 'check' },
          { label: 'Add card details', icon: 'card', badge: 'edited' },
          { label: 'Redirect to Stripe', icon: 'lock' },
          { label: 'Card confirmed', icon: 'check' },
          { label: 'Show dashboard', icon: 'dashboard', kind: 'end' },
        ]} />
      </Lane>

      <Lane n="3" title="Course creation" sub="Upload material, then build automatically or with guidance"
        action={{ label: 'Start course creation', icon: 'plus', onClick: () => {
          if (!guardAction('action.workflow.startCourseWizard')) return;
          onCreateCourse();
        } }}>
        <FSeq nodes={[
          { label: 'Create course', icon: 'course' },
          { label: 'Upload course info', icon: 'upload' },
          { label: 'Submit docs', icon: 'doc' },
          { label: 'Auto or guided?', icon: 'branch', kind: 'decision' },
        ]} />
        <FBranch rows={[
          { label: 'Auto', nodes: [
            { label: 'Confirm domains', icon: 'globe' },
            { label: 'Confirm template', icon: 'assess' },
            { label: 'Yaki builds', icon: 'sparkles' },
            { label: 'Review course', icon: 'eye' },
            { label: 'Baseline assessment', icon: 'target' },
            { label: 'Settings', icon: 'cog' },
            { label: 'Course live', icon: 'check', kind: 'end' },
          ] },
          { label: 'Guided', nodes: [
            { label: 'Domains & subdomains', icon: 'layers', badge: 'new' },
            { label: 'Build outline', icon: 'layers', badge: 'new' },
            { label: 'Add assessments', icon: 'assess', badge: 'new' },
            { label: 'Review course', icon: 'eye' },
            { label: 'Baseline assessment', icon: 'target' },
            { label: 'Settings', icon: 'cog' },
            { label: 'Course live', icon: 'check', kind: 'end' },
          ] },
        ]} />
      </Lane>

      <Lane n="4" title="Instructor demo" sub="A loaded sample workspace with guided analyses"
        action={{ label: 'Open demo flow', icon: 'users', onClick: () => {
          if (!guardAction('action.workflow.replayOnboarding')) return;
          onReplay('instructor');
        } }}>
        <FSeq nodes={[
          { label: 'Explore as instructor', icon: 'users' },
          { label: 'Demo account', icon: 'building' },
          { label: 'Yaki opens sample', icon: 'sparkles', kind: 'decision' },
        ]} />
        <FBranch rows={[
          { label: '', nodes: [{ label: 'Analyse baseline results', icon: 'assess' }, { label: 'Dashboard', icon: 'dashboard', kind: 'end' }] },
          { label: '', nodes: [{ label: 'Identify learners needing support', icon: 'users' }, { label: 'Dashboard', icon: 'dashboard', kind: 'end' }] },
          { label: '', nodes: [{ label: 'Analyse course', icon: 'course', badge: 'edited' }, { label: 'Dashboard', icon: 'dashboard', kind: 'end' }] },
          { label: '', nodes: [{ label: 'Analyse a learner', icon: 'eye' }, { label: 'Dashboard', icon: 'dashboard', kind: 'end' }] },
          { label: '', nodes: [{ label: 'Customise account', icon: 'cog' }, { label: 'Dashboard', icon: 'dashboard', kind: 'end' }] },
        ]} />
      </Lane>

      <Lane n="5" title="Learner preview & contact" sub="Both paths now resolve to a clear end state"
        action={{ label: 'View these paths', icon: 'eye', onClick: () => {
          if (!guardAction('action.workflow.replayOnboarding')) return;
          onReplay('options');
        } }}>
        <FSeq nodes={[{ label: 'Explore as learner', icon: 'eye' }]} />
        <FBranch rows={[
          { label: 'Learner', nodes: [{ label: 'Assistant website + credentials', icon: 'download' }, { label: 'Enter learner app', icon: 'check', kind: 'end', badge: 'new' }] },
          { label: 'Contact', nodes: [{ label: 'Request form', icon: 'mail' }, { label: 'Rep follows up', icon: 'check', kind: 'end', badge: 'new' }] },
        ]} />
      </Lane>

      <div className="fm__lane">
        <div className="fm__lane-head">
          <span className="fm__lane-num" style={{ background: 'var(--info)' }}><Icon name="edit" size={15} /></span>
          <div><h3>What changed from the original diagram</h3><p>Six fixes applied while rebuilding the Visio workflow.</p></div>
        </div>
        <div className="fm__changes">
          {[
            ['branch', 'Reconnected “Analyse course”', "It was floating with no connector in the Visio file — now it branches from Yaki's sample like its sibling analyses."],
            ['x', 'Removed the stray “Yes” label', 'An orphaned text node with no connections; cleaned up.'],
            ['check', 'Added missing end states', 'Learner, Contact, and every analysis leaf now resolve to a clear terminal instead of dead-ending.'],
            ['layers', 'Drew the Guided branch', 'The Auto/Guided decision only had the Auto path — the Guided build path is now fully specified.'],
            ['card', 'Reordered trial billing', 'Packages are shown before card details are collected, so users pick a plan first.'],
            ['edit', 'Fixed the typos', '“Terms of Use”, “Identify”, “Domain”, “Course”, “Develop Baseline Assessment” and more.'],
          ].map(([ic, h, p]) => (
            <div className="change" key={h}>
              <div className="change__ico"><Icon name={ic} size={17} /></div>
              <div><h4>{h}</h4><p>{p}</p></div>
            </div>
          ))}
        </div>
      </div>
    </Wrap>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
